/**
 * Helpers for the SME IPO Reviews page (/sme-ipo-reviews): broker research calls
 * for SME issues from GET /api/core/ipos/sme/reviews.
 *
 * Each item is expected in the same shape as the per-IPO endpoint
 * GET /api/core/ipos/{id}/broker-reviews -> data:
 *   { ipo_id, company_name, reviews: [{ broker_name, rating, source_url, summary, ... }],
 *     summary: { total_reviews, consensus, rating_counts } }
 * Common alternative field names are accepted too, so a slightly different
 * payload still maps. The backend decides which issues are SME.
 */
import { CONSENSUS, consensusBucket } from './ipoReviews';
import { RATING_TONE, consensusFromRatingCounts, toIsoDate } from './ipoCatalogAdapters';
import { toGmpSlug } from './gmpDataMapper';
import { PLATFORMS, matchKey } from './smeIpos';

// Third-party research links: only ever render http(s) URLs
const safeUrl = (value) => (/^https?:\/\//i.test(String(value || '')) ? value : null);

const exchangeFromText = (text) => {
  if (/bse/i.test(text || '')) return 'BSE';
  if (/nse|emerge/i.test(text || '')) return 'NSE';
  return null;
};

const firstText = (...values) => values.map((value) => (typeof value === 'string' ? value.trim() : '')).find(Boolean) || null;

const countCalls = (brokers) =>
  brokers.reduce((counts, broker) => ({ ...counts, [broker.call]: (counts[broker.call] || 0) + 1 }), {});

const mapBrokers = (reviews) =>
  (Array.isArray(reviews) ? reviews : [])
    .map((review) => ({ ...review, broker_name: firstText(review?.broker_name, review?.broker, review?.analyst, review?.name) }))
    .filter((review) => review.broker_name)
    .map((review) => {
      const call = firstText(review.rating, review.call, review.recommendation) || 'Not Rated';
      return {
        name: review.broker_name,
        call,
        tone: RATING_TONE[call] || 'neutral',
        sourceUrl: safeUrl(review.source_url || review.url || review.report_url),
        summary: review.summary || '',
      };
    });

/**
 * @param {object} data
 * @param {object[]} data.items - GET /api/core/ipos/sme/reviews -> data.items
 * @param {object[]} data.smeIpos - rows from utils/smeIpos.js buildSmeIpoRows, for
 *   the platform, bidding status and GMP link of issues that are live or upcoming
 */
export const mapSmeReviewItems = ({ items = [], smeIpos = [] }) => {
  const smeIndex = new Map(smeIpos.map((row) => [matchKey(row.company), row]));
  const byCompany = new Map();

  (Array.isArray(items) ? items : []).forEach((item) => {
    const company = firstText(item?.company_name, item?.companyName, item?.company);
    if (!company) return;
    const brokers = mapBrokers(item.reviews || item.broker_reviews);
    const summary = item.summary && typeof item.summary === 'object' ? item.summary : {};
    const live = smeIndex.get(matchKey(company));
    const exchange = exchangeFromText(firstText(item.exchange, item.listing_exchange, item.platform)) || live?.exchange || null;
    const noteSource = brokers.find((broker) => broker.sourceUrl);
    const updatedOn =
      toIsoDate(firstText(item.updated_at, item.reviewed_at, item.refreshed_at, item.created_at)) || null;

    const row = {
      id: String(item.ipo_id ?? item.id ?? item.symbol ?? company),
      company,
      sector: firstText(item.sector, item.industry, Array.isArray(item.product_categories) ? item.product_categories[0] : null),
      exchange,
      platform: exchange ? PLATFORMS[exchange] : 'SME',
      status: live?.status || null,
      brokers,
      reviewCount: brokers.length,
      subscribeCalls: brokers.filter((broker) => broker.call === 'Subscribe').length,
      consensus: consensusFromRatingCounts(summary.rating_counts || countCalls(brokers), brokers.length),
      consensusText: brokers.length ? firstText(summary.consensus) : null,
      cmScore: typeof item.cm_score === 'number' ? item.cm_score : null,
      noteUrl: noteSource?.sourceUrl || null,
      noteBroker: noteSource?.name || null,
      updatedOn,
      gmpSlug: live?.gmpSlug || toGmpSlug(company),
    };

    // The same company can appear twice: keep the entry with more broker calls
    const key = matchKey(company);
    const existing = byCompany.get(key);
    if (!existing || row.reviewCount > existing.reviewCount) byCompany.set(key, row);
  });

  return [...byCompany.values()].sort(compareSmeReviews);
};

// Issues with broker calls first, then the most recently updated
export const compareSmeReviews = (a, b) =>
  Number(b.reviewCount > 0) - Number(a.reviewCount > 0) ||
  (b.updatedOn || '').localeCompare(a.updatedOn || '') ||
  a.company.localeCompare(b.company);

export const VERDICT_OPTIONS = [
  { key: 'apply', label: 'Apply / Subscribe' },
  { key: 'may', label: 'Neutral / May Apply' },
  { key: 'avoid', label: 'Avoid' },
  { key: 'awaited', label: 'Awaiting Reviews' },
];

export const consensusLabel = (row) => (row.consensus === 'awaited' ? 'Awaiting Reviews' : CONSENSUS[row.consensus].label);

export const brokerNames = (rows) =>
  [...new Set(rows.flatMap((row) => row.brokers.map((broker) => broker.name)))].sort((a, b) => a.localeCompare(b));

export const filterSmeReviews = (rows, { query = '', broker = 'all', verdict = 'all' } = {}) => {
  const needle = query.trim().toLowerCase();
  return rows
    .filter((row) => !needle || `${row.company} ${row.sector || ''}`.toLowerCase().includes(needle))
    .filter((row) => broker === 'all' || row.brokers.some((entry) => entry.name === broker))
    .filter((row) => verdict === 'all' || consensusBucket(row) === verdict);
};

export const computeSmeReviewStats = (rows) => {
  const reviewed = rows.filter((row) => row.reviewCount > 0);
  const bullish = reviewed.filter((row) => consensusBucket(row) === 'apply');

  const callsByBroker = new Map();
  reviewed.forEach((row) => row.brokers.forEach((broker) => callsByBroker.set(broker.name, (callsByBroker.get(broker.name) || 0) + 1)));
  const topBrokers = [...callsByBroker.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([name]) => name);

  // Highest share of Subscribe calls, then the most brokers behind it
  const topPick = bullish.reduce((best, row) => {
    if (!best) return row;
    const share = row.subscribeCalls / row.reviewCount - best.subscribeCalls / best.reviewCount;
    return share > 0 || (share === 0 && row.reviewCount > best.reviewCount) ? row : best;
  }, null);

  return {
    tracked: rows.length,
    reviewedCount: reviewed.length,
    bullishCount: bullish.length,
    bullishShare: reviewed.length ? (bullish.length / reviewed.length) * 100 : null,
    deskCount: callsByBroker.size,
    topBrokers: topBrokers.slice(0, 3),
    topPick,
  };
};

const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildSmeReviewsCsv = (rows) => {
  const header = ['Company', 'Sector', 'Platform', 'Broker Calls', 'Consensus', 'Consensus Detail', 'CM Score', 'Research Note', 'Last Updated'];
  const lines = rows.map((row) =>
    [
      row.company,
      row.sector,
      row.platform,
      row.brokers.map((broker) => `${broker.name}: ${broker.call}`).join('; '),
      consensusLabel(row),
      row.consensusText,
      row.cmScore,
      row.noteUrl,
      row.updatedOn,
    ]
      .map(csvCell)
      .join(',')
  );
  return [header.map(csvCell).join(','), ...lines].join('\r\n');
};
